import { ReactNode } from 'react';
import { Box, Button, Chip, CircularProgress, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import PreviewProduct from './PreviewProduct';
import SetupSection from './SetupSection';
import { Icon, IconSize } from '@shared/icons';

interface PreviewRow {
  title: string;
  imageUrl?: string;
  fieldValues: Record<string, string | null>;
}

interface PreviewMatch {
  title: string;
  image?: string;
  ean?: string;
  sku?: string;
}

interface ImportPreviewProps {
  marketplaceLabel: string;
  sourceFields: { path: string; label: string }[];
  sourceField: string;
  products: PreviewRow[];
  matches: Record<string, PreviewMatch>;
  duplicateValues?: string[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
}

const ImportPreview = ({
  marketplaceLabel,
  sourceFields,
  sourceField,
  products,
  matches,
  duplicateValues = [],
  loading,
  error,
  hasMore,
  onLoadMore,
}: ImportPreviewProps) => {
  const theme = useTheme();
  const duplicates = new Set(duplicateValues);


  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, borderTop: `1px solid ${theme.palette.divider}` }}>
      <SetupSection title="Import Preview">
        {error && <Typography color="error" variant="body2">{error}</Typography>}

        <Paper variant="outlined">
          <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{marketplaceLabel}</Typography>
            <Typography variant="body2" color="text.secondary">JustStock</Typography>
          </Stack>
          <Divider />
          {loading && products.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : products.map((product, index) => {
            const sourceLabel = sourceFields.find(field => field.path === sourceField)?.label ?? sourceField;
            const value = product.fieldValues[sourceField]?.trim();
            const match = value ? matches[value] : undefined;
            const isDuplicate = !!value && duplicates.has(value);

            return (
              <Box key={`${product.title}-${index}`}>
                <Stack direction="row" alignItems="center" gap={2} sx={{ py: 2 }}>

                  <PreviewProduct title={product.title} image={product.imageUrl} detail={value ? `${sourceLabel}: ${value}` : ''} />


                  <Icon icon="ArrowRight" size={IconSize.SMALL} color={theme.palette.info.main} />

                  <Box sx={{ flex: 1 }}>


                    {!value
                      ? <Chip label={`No ${sourceLabel}`} size="small" color="error" sx={{ borderRadius: '6px' }} />
                      : isDuplicate
                        ? <Chip label="Duplicate value. This product won't be imported." variant="outlined" color="error" />
                        : match
                          ? <PreviewProduct title={match.title} image={match.image} detail={match.sku ? `SKU: ${match.sku}` : match.ean ? `EAN: ${match.ean}` : ''} />
                          : <Chip label="New product" size="small" color="primary" sx={{ borderRadius: '6px' }} />}
                  </Box>

                </Stack>
              </Box>
            );
          })}
          {hasMore && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <Button variant="text" size="small" onClick={onLoadMore} disabled={loading}>
                  {loading ? <CircularProgress size={16} /> : 'Load more...'}
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </SetupSection>
    </Box>
  );
}

export default ImportPreview;
